import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { queryClient } from "~/lib/query-client";
import { Skeleton, useToast } from "@alchema/ui";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import { useResumeStore } from "~/stores/resume-store";
import type { ResumeData } from "@alchema/shared";
import * as m from "~/paraglide/messages";

const searchSchema = z.object({
  resumeId: z.string().optional(),
  from: z.enum(["resume"]).optional(),
});
type KeywordOptimizerSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/keyword-optimizer")({
  validateSearch: (search): KeywordOptimizerSearch => searchSchema.parse(search),
  beforeLoad: async () => {
    await requireAuth();
  },
  loader: async () => {
    await Promise.all([
      queryClient.prefetchQuery(orpc.job.list.queryOptions({ input: undefined })),
      queryClient.prefetchQuery(orpc.resume.list.queryOptions({ input: undefined })),
    ]);
    return null;
  },
  component: KeywordOptimizerPage,
  pendingComponent: KeywordOptimizerSkeleton,
  errorComponent: RouteErrorBoundary,
});

// ---------------------------------------------------------------------------
// Types (local to this file)
// ---------------------------------------------------------------------------

type KeywordStatus = "present" | "injected" | "missing";
type ClicheSeverity = "high" | "medium" | "low";
type CategoryTab = "all" | "technicalSkills" | "softSkills" | "industryTerms";

interface KeywordDistribution {
  keyword: string;
  locations: string[];
  status: KeywordStatus;
}

interface ClicheReport {
  phrase: string;
  location: string;
  suggestion: string;
  severity: ClicheSeverity;
}

interface Reformulation {
  original: string;
  reformulated: string;
  injectedKeywords: string[];
}

interface AnalysisResult {
  extraction: {
    keywords: string[];
    technicalSkills: string[];
    softSkills: string[];
    industryTerms: string[];
  };
  distribution: KeywordDistribution[];
  cliches: ClicheReport[];
  reformulations: Reformulation[];
  coverage: {
    total: number;
    present: number;
    injected: number;
    missing: number;
    percentage: number;
  };
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function KeywordOptimizerSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-10 rounded-lg" />
      </div>
      <Skeleton className="mt-6 h-40 rounded-lg" />
      <Skeleton className="mt-4 h-64 rounded-lg" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<KeywordStatus, { bg: string; text: string; dot: string; label: () => string }> = {
  present: {
    bg: "transparent",
    text: "var(--text)",
    dot: "var(--success)",
    label: () => m.keyword_optimizer_status_present(),
  },
  injected: {
    bg: "transparent",
    text: "var(--text)",
    dot: "var(--accent)",
    label: () => m.keyword_optimizer_status_injected(),
  },
  missing: {
    bg: "transparent",
    text: "var(--text-muted)",
    dot: "var(--error)",
    label: () => m.keyword_optimizer_status_missing(),
  },
};

const SEVERITY_STYLES: Record<ClicheSeverity, { bg: string; text: string }> = {
  high: { bg: "var(--error-subtle, rgba(239,68,68,0.1))", text: "var(--error)" },
  medium: { bg: "var(--warning-subtle, rgba(234,179,8,0.1))", text: "var(--warning)" },
  low: { bg: "var(--surface-raised)", text: "var(--text-muted)" },
};

const CATEGORY_LABELS: Record<CategoryTab, () => string> = {
  all: () => m.keyword_optimizer_category_all(),
  technicalSkills: () => m.keyword_optimizer_category_technical_skills(),
  softSkills: () => m.keyword_optimizer_category_soft_skills(),
  industryTerms: () => m.keyword_optimizer_category_industry_terms(),
};

function getFilteredKeywords(
  distribution: KeywordDistribution[],
  extraction: AnalysisResult["extraction"],
  tab: CategoryTab,
): KeywordDistribution[] {
  if (tab === "all") return distribution;
  const categoryKeywords = new Set(extraction[tab].map((k) => k.toLowerCase()));
  return distribution.filter((d) => categoryKeywords.has(d.keyword.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

type ReformulationDecision = "accepted" | "rejected";

function KeywordOptimizerPage() {
  const { toast } = useToast();
  const resumeStore = useResumeStore();
  const search = Route.useSearch();
  const cameFromResume = search.from === "resume";

  const [selectedJobId, setSelectedJobId] = React.useState("");
  const [selectedResumeId, setSelectedResumeId] = React.useState(search.resumeId ?? "");
  const [activeTab, setActiveTab] = React.useState<CategoryTab>("all");
  const [result, setResult] = React.useState<AnalysisResult | null>(null);
  const [reformulationDecisions, setReformulationDecisions] = React.useState<Record<number, ReformulationDecision>>({});
  const jobSelectRef = React.useRef<HTMLSelectElement>(null);

  // Fetch jobs and resumes for the selectors
  const jobsQuery = useQuery(orpc.job.list.queryOptions({ input: undefined }));
  const resumesQuery = useQuery(orpc.resume.list.queryOptions({ input: undefined }));
  // Credits + tier for gating the AI "Suggest Rewording" call on the client
  // (the server already gates, but pre-flight gives a much better UX than
  // letting the user click, hit a silent error, and lose trust.)
  const creditsQuery = useQuery(orpc.credits.getBalance.queryOptions());

  const jobs = jobsQuery.data ?? [];
  const resumes = resumesQuery.data ?? [];
  const credits = creditsQuery.data;
  // Fail-open philosophy: if the credit query is slow or fails, DON'T block
  // the user — the server still enforces tier/credit limits authoritatively.
  // Blocking on client-side data that never arrives was causing silent
  // "button doesn't do anything" bugs.
  const creditsLoaded = creditsQuery.isSuccess;
  const isFreeTier = creditsLoaded && credits?.tier === "free";
  const isOutOfCredits =
    creditsLoaded && (credits?.limit ?? 0) > 0 && (credits?.balance ?? 0) <= 0;
  const showUpgradeGate = isFreeTier || isOutOfCredits;
  const canAffordInject = creditsLoaded && !isFreeTier && !isOutOfCredits;

  // Analyze mutation (deterministic, no credits)
  const analyzeMutation = useMutation(
    orpc.keywords.analyze.mutationOptions({
      onSuccess: (data) => {
        setResult(data as AnalysisResult);
        setReformulationDecisions({});
        toast(m.keyword_optimizer_toast_analysis_complete(), "success");
      },
      onError: (err) => {
        toast(err.message || m.keyword_optimizer_toast_analysis_failed(), "error");
      },
    }),
  );

  // Inject mutation (AI, costs credits)
  const injectMutation = useMutation(
    orpc.keywords.inject.mutationOptions({
      onSuccess: (data) => {
        setResult(data as AnalysisResult);
        setReformulationDecisions({});
        toast(m.keyword_optimizer_toast_injection_complete(), "success");
      },
      onError: (err) => {
        toast(err.message || m.keyword_optimizer_toast_injection_failed(), "error");
      },
    }),
  );

  const isLoading = analyzeMutation.isPending || injectMutation.isPending;
  const canAnalyze = selectedJobId && !isLoading;
  const canInject = canAnalyze && result !== null;

  // Auto-focus the job selector when the user arrives from the resume page
  // with resume pre-selected — their only remaining action is "pick a job."
  React.useEffect(() => {
    if (cameFromResume && selectedResumeId && !selectedJobId) {
      jobSelectRef.current?.focus();
    }
  }, [cameFromResume, selectedResumeId, selectedJobId]);

  // Count accepted reformulations for the "Back to resume" progress bar
  const acceptedCount = Object.values(reformulationDecisions).filter(
    (d) => d === "accepted",
  ).length;
  const totalReformulations = result?.reformulations.length ?? 0;
  const showBackToResume = cameFromResume && acceptedCount > 0;
  const targetResumeIdForReturn =
    selectedResumeId || resumeStore.getMasterResume()?.id || "";

  function handleAnalyze() {
    if (!selectedJobId) return;
    analyzeMutation.mutate({
      jobId: selectedJobId,
      resumeId: selectedResumeId || undefined,
    });
  }

  function handleInject() {
    if (!selectedJobId) return;
    injectMutation.mutate({
      jobId: selectedJobId,
      resumeId: selectedResumeId || undefined,
    });
  }

  // Pure search-and-replace over a resume data object. Returns the new data
  // and whether any replacement was actually applied. Kept pure so it works
  // for both single-accept and bulk Apply-All paths.
  function applyReformulationToData(
    data: ResumeData,
    reformulation: Reformulation,
  ): { data: ResumeData; applied: boolean } {
    const original = reformulation.original.trim();
    if (!original) return { data, applied: false };

    // Try summary first
    if (data.summary?.includes(original)) {
      return {
        data: { ...data, summary: data.summary.replace(original, reformulation.reformulated) },
        applied: true,
      };
    }

    // Then work entries (summary field + highlights)
    const work = data.work ?? [];
    for (let i = 0; i < work.length; i++) {
      const entry = work[i];
      if (entry.summary?.includes(original)) {
        const next = [...work];
        next[i] = { ...entry, summary: entry.summary.replace(original, reformulation.reformulated) };
        return { data: { ...data, work: next }, applied: true };
      }
      const hIdx = (entry.highlights ?? []).findIndex((h) => h.includes(original));
      if (hIdx !== -1) {
        const newHighlights = [...(entry.highlights ?? [])];
        newHighlights[hIdx] = newHighlights[hIdx].replace(original, reformulation.reformulated);
        const next = [...work];
        next[i] = { ...entry, highlights: newHighlights };
        return { data: { ...data, work: next }, applied: true };
      }
    }

    return { data, applied: false };
  }

  // Persist resume data via server mutation. Without this, local Zustand
  // changes get overwritten by the resume page's syncFromServer on return.
  const resumeUpdateMutation = useMutation(
    orpc.resume.update.mutationOptions({
      onSuccess: (updated) => {
        resumeStore.syncFromServer(updated as Parameters<typeof resumeStore.syncFromServer>[0]);
      },
      onError: (err) => {
        toast(err.message || m.keyword_optimizer_toast_save_failed(), "error");
      },
    }),
  );

  function persistResume(targetResumeId: string, nextData: ResumeData) {
    // Optimistic local update via syncFromServer so the UI reflects the
    // change instantly; the mutation's onSuccess re-syncs with the
    // authoritative server copy afterward.
    const current = resumeStore.resumes.find((r) => r.id === targetResumeId);
    if (current) {
      resumeStore.syncFromServer({
        ...current,
        data: nextData,
        updatedAt: new Date().toISOString(),
      });
    }
    resumeUpdateMutation.mutate({ id: targetResumeId, data: nextData });
  }

  function handleAcceptReformulation(index: number, reformulation: Reformulation) {
    const targetResumeId = selectedResumeId || resumeStore.getMasterResume()?.id;
    if (!targetResumeId) {
      toast(m.keyword_optimizer_toast_no_resume(), "error");
      return;
    }
    const resume = resumeStore.resumes.find((r) => r.id === targetResumeId);
    if (!resume) {
      toast(m.keyword_optimizer_toast_no_resume(), "error");
      return;
    }

    const { data: nextData, applied } = applyReformulationToData(resume.data, reformulation);

    if (applied) {
      persistResume(targetResumeId, nextData);
      setReformulationDecisions((prev) => ({ ...prev, [index]: "accepted" }));
      toast(m.keyword_optimizer_toast_accepted(), "success");
    } else {
      // Don't mark as accepted if nothing changed — user should know the
      // AI-reformulated text didn't match anything in the resume (common
      // when the AI slightly rewords the "original" side).
      toast(m.keyword_optimizer_toast_apply_failed(), "error");
    }
  }

  function handleApplyAll() {
    if (!result) return;
    const targetResumeId = selectedResumeId || resumeStore.getMasterResume()?.id;
    if (!targetResumeId) {
      toast(m.keyword_optimizer_toast_no_resume(), "error");
      return;
    }
    const resume = resumeStore.resumes.find((r) => r.id === targetResumeId);
    if (!resume) {
      toast(m.keyword_optimizer_toast_no_resume(), "error");
      return;
    }

    // Chain all non-decided reformulations through the pure helper so a
    // single server write captures the full batch.
    let currentData = resume.data;
    let appliedCount = 0;
    const newDecisions: Record<number, ReformulationDecision> = { ...reformulationDecisions };
    result.reformulations.forEach((r, idx) => {
      if (reformulationDecisions[idx]) return; // skip already accepted/rejected
      const { data: nextData, applied } = applyReformulationToData(currentData, r);
      if (applied) {
        currentData = nextData;
        appliedCount += 1;
        newDecisions[idx] = "accepted";
      }
    });

    if (appliedCount === 0) {
      toast(m.keyword_optimizer_toast_apply_failed(), "error");
      return;
    }

    persistResume(targetResumeId, currentData);
    setReformulationDecisions(newDecisions);
    toast(m.keyword_optimizer_toast_applied_bulk({ count: String(appliedCount) }), "success");
  }

  function handleRejectReformulation(index: number) {
    setReformulationDecisions((prev) => ({ ...prev, [index]: "rejected" }));
    toast(m.keyword_optimizer_toast_rejected(), "default");
  }

  const filteredKeywords = result
    ? getFilteredKeywords(result.distribution, result.extraction, activeTab)
    : [];

  return (
    <div className="animate-fade-up mx-auto max-w-5xl px-4 py-8">
      {/* Header */}
      <div className="page-header mb-6">
        <h1
          className="font-semibold leading-tight"
          style={{ fontSize: "22px", color: "var(--text)" }}
        >
          {m.keyword_optimizer_page_title()}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          {m.keyword_optimizer_page_description()}
        </p>
      </div>

      {/* Split view: selectors */}
      <div className="flex gap-6">
        {/* Resume selector */}
        <div
          className="flex-1 rounded-lg p-4"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <p className="section-label mb-3">{m.keyword_optimizer_label_resume()}</p>
          <select
            id="kw-resume-select"
            value={selectedResumeId}
            onChange={(e) => setSelectedResumeId(e.target.value)}
            className="w-full rounded-md px-3 py-2 text-sm outline-none focus:ring-1"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text)",
            }}
            disabled={resumesQuery.isLoading}
          >
            <option value="">{m.keyword_optimizer_placeholder_master_resume()}</option>
            {resumes.map((r: { id: string; title?: string | null; isMaster?: boolean }) => (
              <option key={r.id} value={r.id}>
                {r.title?.trim() || m.keyword_optimizer_untitled_resume()}
                {r.isMaster ? " ★" : ""}
              </option>
            ))}
          </select>
          {!resumesQuery.isLoading && resumes.length === 0 && (
            <p className="mt-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
              {m.keyword_optimizer_no_resumes_hint()}
            </p>
          )}
        </div>

        {/* Job selector */}
        <div
          className="flex-1 rounded-lg p-4"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <p className="section-label mb-3">{m.keyword_optimizer_label_job_description()}</p>
          <select
            id="kw-job-select"
            ref={jobSelectRef}
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            className="w-full rounded-md px-3 py-2 text-sm outline-none focus:ring-1"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text)",
            }}
            disabled={jobsQuery.isLoading}
          >
            <option value="">{m.keyword_optimizer_placeholder_select_job()}</option>
            {jobs.map((j: { id: string; title?: string | null; company?: string | null }) => (
              <option key={j.id} value={j.id}>
                {j.title || m.keyword_optimizer_untitled_job()} {j.company ? `- ${j.company}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* How it works */}
      {!result && !isLoading && (
        <HowItWorks />
      )}

      {/* Action buttons */}
      <div className="mt-4 flex gap-3 items-start">
        <div className="flex flex-col items-start gap-1">
          <button
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            className="btn btn-primary"
          >
            {analyzeMutation.isPending ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner /> {m.keyword_optimizer_button_analyzing()}
              </span>
            ) : (
              m.keyword_optimizer_button_analyze()
            )}
          </button>
          <span className="text-[10px] pl-1" style={{ color: "var(--text-muted)" }}>
            {m.keyword_optimizer_analyze_hint()}
          </span>
        </div>
        <div className="flex flex-col items-start gap-1">
          <button
            onClick={handleInject}
            disabled={!canInject}
            className="btn btn-secondary"
          >
            {injectMutation.isPending ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner /> {m.keyword_optimizer_button_injecting()}
              </span>
            ) : (
              m.keyword_optimizer_button_inject()
            )}
          </button>
          <span className="text-[10px] pl-1" style={{ color: "var(--text-muted)" }}>
            {m.keyword_optimizer_inject_hint()}
          </span>
        </div>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="mt-8 space-y-4">
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-48 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      )}

      {/* Results */}
      {result && !isLoading && (
        <div className="mt-8 space-y-6">
          {/* Reformulations — most important, shown first */}
          {result.reformulations.length > 0 ? (
            <ReformulationPreview
              reformulations={result.reformulations}
              decisions={reformulationDecisions}
              onAccept={handleAcceptReformulation}
              onReject={handleRejectReformulation}
              onApplyAll={handleApplyAll}
              isPersisting={resumeUpdateMutation.isPending}
            />
          ) : result.coverage.missing === 0 && result.coverage.total > 0 ? (
            <div
              className="rounded-lg p-4 text-sm"
              style={{
                background: "var(--success-subtle, rgba(34,197,94,0.06))",
                color: "var(--success)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              {m.keyword_optimizer_no_reformulations()}
            </div>
          ) : result.coverage.missing > 0 ? (
            // Analyze finished and found missing keywords but produced no
            // reformulations — the deterministic analyzer can only rewrite
            // when it finds obvious near-matches. The AI-powered "Suggest
            // Rewording" step is what actually generates suggestions users
            // can accept. Make that the obvious next action, but gate it
            // behind tier + credit balance so free / exhausted users get a
            // clear upgrade path instead of a silent server rejection.
            <div
              className="rounded-lg p-5"
              style={{
                background: "var(--accent-subtle, rgba(38,109,240,0.06))",
                border: "1px solid var(--accent)",
              }}
              data-testid="generate-suggestions-cta"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                    {m.keyword_optimizer_generate_cta_title({
                      count: String(result.coverage.missing),
                    })}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                    {isFreeTier
                      ? m.keyword_optimizer_generate_cta_upgrade_hint()
                      : isOutOfCredits
                        ? m.keyword_optimizer_generate_cta_out_of_credits_hint({
                            resetAt: credits ? new Date(credits.resetAt).toLocaleDateString() : "",
                          })
                        : m.keyword_optimizer_generate_cta_hint()}
                  </p>
                  {canAffordInject && credits && (
                    <p className="mt-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                      {m.keyword_optimizer_generate_cta_credits_remaining({
                        balance: String(credits.balance),
                        limit: String(credits.limit),
                      })}
                    </p>
                  )}
                  {/* Inline error so users don't miss a brief toast. */}
                  {injectMutation.isError && (
                    <p
                      className="mt-2 rounded-md px-3 py-2 text-xs font-medium"
                      style={{
                        background: "var(--error-subtle, rgba(239,68,68,0.1))",
                        color: "var(--error)",
                        border: "1px solid var(--error)",
                      }}
                      role="alert"
                      data-testid="generate-suggestions-error"
                    >
                      {injectMutation.error instanceof Error
                        ? injectMutation.error.message
                        : m.keyword_optimizer_toast_injection_failed()}
                    </p>
                  )}
                  {/* Succeeded but AI returned zero suggestions — common
                      when the resume has no bullets that overlap with JD
                      vocabulary. Explicitly tell the user so they don't
                      think the button is broken. */}
                  {injectMutation.isSuccess &&
                    result.reformulations.length === 0 && (
                      <p
                        className="mt-2 rounded-md px-3 py-2 text-xs font-medium"
                        style={{
                          background: "var(--warning-subtle, rgba(234,179,8,0.1))",
                          color: "var(--warning)",
                          border: "1px solid var(--warning)",
                        }}
                        role="status"
                        data-testid="generate-suggestions-empty"
                      >
                        {m.keyword_optimizer_generate_cta_empty_result()}
                      </p>
                    )}
                </div>
                {showUpgradeGate ? (
                  <Link
                    to="/pricing"
                    className="btn btn-primary shrink-0"
                    data-testid="generate-suggestions-upgrade"
                  >
                    {m.keyword_optimizer_generate_cta_upgrade_button()}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={handleInject}
                    disabled={!canInject || injectMutation.isPending}
                    className="btn btn-primary shrink-0"
                    data-testid="generate-suggestions-button"
                  >
                    {injectMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner /> {m.keyword_optimizer_button_injecting()}
                      </span>
                    ) : (
                      m.keyword_optimizer_generate_cta_button()
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Coverage Score */}
          <CoverageScore coverage={result.coverage} />

          {/* Keyword split view */}
          <KeywordSplitView
            keywords={result.distribution}
            extraction={result.extraction}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            filteredKeywords={filteredKeywords}
          />

          {/* Cliche Scanner */}
          {result.cliches.length > 0 && <ClicheTable cliches={result.cliches} />}
        </div>
      )}

      {/* Sticky return-to-resume bar — only shown when user arrived from the
          resume page AND has accepted at least one reformulation. Gives the
          primary "see my improved score" CTA without competing with the
          individual accept/reject buttons. */}
      {showBackToResume && targetResumeIdForReturn && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t"
          style={{
            background: "var(--surface)",
            borderColor: "var(--border-subtle)",
            boxShadow: "0 -8px 24px rgba(0,0,0,0.08)",
          }}
          data-testid="back-to-resume-bar"
        >
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
            <span className="text-sm" style={{ color: "var(--text)" }}>
              {m.keyword_optimizer_applied_count({
                count: String(acceptedCount),
                total: String(totalReformulations),
              })}
            </span>
            <Link
              to="/resumes/$resumeId"
              params={{ resumeId: targetResumeIdForReturn }}
              className="btn btn-primary"
            >
              {m.keyword_optimizer_back_to_resume()}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HowItWorks() {
  const steps = [
    { num: "1", title: m.keyword_optimizer_step1_title(), desc: m.keyword_optimizer_step1_desc() },
    { num: "2", title: m.keyword_optimizer_step2_title(), desc: m.keyword_optimizer_step2_desc() },
    { num: "3", title: m.keyword_optimizer_step3_title(), desc: m.keyword_optimizer_step3_desc() },
  ];

  return (
    <div
      className="mt-6 rounded-lg p-5"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <p className="section-label mb-4">{m.keyword_optimizer_how_it_works_title()}</p>
      <div className="grid gap-5 md:grid-cols-3">
        {steps.map((step) => (
          <div key={step.num} className="flex gap-3">
            <span
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums"
              style={{ background: "var(--accent)", color: "#fff" }}
            >
              {step.num}
            </span>
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{step.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function CoverageScore({ coverage }: { coverage: AnalysisResult["coverage"] }) {
  const pct = Math.round(coverage.percentage);
  const barColor = pct >= 70 ? "var(--success)" : pct >= 40 ? "var(--warning)" : "var(--error)";

  return (
    <div
      className="rounded-lg p-5"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <p className="section-label mb-4">{m.keyword_optimizer_coverage_title()}</p>
      <div className="flex items-end justify-between gap-6">
        {/* Big stat */}
        <div>
          <span
            className="font-semibold tabular-nums leading-none"
            style={{ fontSize: "28px", color: barColor }}
          >
            {pct}%
          </span>
          <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            {m.keyword_optimizer_coverage_title()}
          </p>
        </div>

        {/* Breakdown */}
        <div className="flex gap-6 text-xs pb-0.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "var(--success)" }} />
            <span style={{ color: "var(--text-muted)" }}>
              <span className="font-medium tabular-nums" style={{ color: "var(--text)" }}>{coverage.present}</span>
              {" "}{m.keyword_optimizer_coverage_present()}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "var(--accent)" }} />
            <span style={{ color: "var(--text-muted)" }}>
              <span className="font-medium tabular-nums" style={{ color: "var(--text)" }}>{coverage.injected}</span>
              {" "}{m.keyword_optimizer_coverage_injected()}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "var(--error)" }} />
            <span style={{ color: "var(--text-muted)" }}>
              <span className="font-medium tabular-nums" style={{ color: "var(--text)" }}>{coverage.missing}</span>
              {" "}{m.keyword_optimizer_coverage_missing()}
            </span>
          </div>
          <div style={{ color: "var(--text-muted)" }}>
            <span className="font-medium tabular-nums" style={{ color: "var(--text)" }}>{coverage.total}</span>
            {" "}{m.keyword_optimizer_coverage_total()}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="mt-4 h-1.5 w-full overflow-hidden rounded-full"
        style={{ background: "var(--surface-raised)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
    </div>
  );
}

function KeywordSplitView({
  keywords,
  extraction,
  activeTab,
  onTabChange,
  filteredKeywords,
}: {
  keywords: KeywordDistribution[];
  extraction: AnalysisResult["extraction"];
  activeTab: CategoryTab;
  onTabChange: (tab: CategoryTab) => void;
  filteredKeywords: KeywordDistribution[];
}) {
  const matched = filteredKeywords.filter((k) => k.status === "present" || k.status === "injected");
  const missing = filteredKeywords.filter((k) => k.status === "missing");

  return (
    <div>
      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {(Object.keys(CATEGORY_LABELS) as CategoryTab[]).map((tab) => {
          const counts: Record<CategoryTab, number> = {
            all: extraction.keywords.length,
            technicalSkills: extraction.technicalSkills.length,
            softSkills: extraction.softSkills.length,
            industryTerms: extraction.industryTerms.length,
          };
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-150"
              style={{
                background: isActive ? "var(--accent)" : "var(--surface-raised)",
                color: isActive ? "#fff" : "var(--text-muted)",
                border: isActive ? "1px solid transparent" : "1px solid var(--border-subtle)",
              }}
            >
              {CATEGORY_LABELS[tab]()}
              <span className="ml-1 opacity-60">({counts[tab]})</span>
            </button>
          );
        })}
      </div>

      {filteredKeywords.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.keyword_optimizer_empty_no_keywords()}
        </p>
      ) : (
        <div className="flex gap-6">
          {/* Left: Resume keywords (matched) */}
          <div
            className="flex-1 rounded-lg p-4"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <p className="section-label mb-3">{m.keyword_optimizer_distribution_title()} — {m.keyword_optimizer_status_present()}</p>
            {matched.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>—</p>
            ) : (
              <ul className="space-y-1.5">
                {matched.map((kw) => (
                  <li key={kw.keyword} className="flex items-center gap-2 text-sm">
                    <span
                      className="h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ background: STATUS_STYLES[kw.status].dot }}
                    />
                    <span style={{ color: "var(--text)" }}>{kw.keyword}</span>
                    {kw.status === "injected" && (
                      <span
                        className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                        style={{ border: "1px solid var(--border-subtle)", color: "var(--text-muted)" }}
                      >
                        {m.keyword_optimizer_status_injected()}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: Job keywords (missing) */}
          <div
            className="flex-1 rounded-lg p-4"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <p className="section-label mb-3">{m.keyword_optimizer_status_missing()}</p>
            {missing.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--success)" }}>
                {m.keyword_optimizer_no_reformulations()}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {missing.map((kw) => (
                  <span
                    key={kw.keyword}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
                    style={{
                      border: "1px solid var(--border-subtle)",
                      color: "var(--text-muted)",
                      background: "var(--surface-raised)",
                    }}
                    title={`Locations: ${kw.locations.join(", ") || "none"}`}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ background: STATUS_STYLES.missing.dot }}
                    />
                    {kw.keyword}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ClicheTable({ cliches }: { cliches: ClicheReport[] }) {
  return (
    <div
      className="rounded-lg p-4 overflow-x-auto"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <p className="section-label mb-4">{m.keyword_optimizer_cliche_title()}</p>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ color: "var(--text-muted)" }}>
            <th className="text-left pb-2 pr-4 font-medium">{m.keyword_optimizer_cliche_col_phrase()}</th>
            <th className="text-left pb-2 pr-4 font-medium">{m.keyword_optimizer_cliche_col_location()}</th>
            <th className="text-left pb-2 pr-4 font-medium">{m.keyword_optimizer_cliche_col_severity()}</th>
            <th className="text-left pb-2 font-medium">{m.keyword_optimizer_cliche_col_suggestion()}</th>
          </tr>
        </thead>
        <tbody>
          {cliches.map((c, idx) => {
            const sev = SEVERITY_STYLES[c.severity];
            return (
              <tr
                key={idx}
                className="border-t"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <td className="py-2.5 pr-4 font-medium" style={{ color: "var(--text)" }}>
                  {c.phrase}
                </td>
                <td className="py-2.5 pr-4" style={{ color: "var(--text-muted)" }}>
                  {c.location}
                </td>
                <td className="py-2.5 pr-4">
                  <span
                    className="inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                    style={{ background: sev.bg, color: sev.text }}
                  >
                    {c.severity}
                  </span>
                </td>
                <td className="py-2.5" style={{ color: "var(--text-secondary)" }}>
                  {c.suggestion}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ReformulationPreview({
  reformulations,
  decisions,
  onAccept,
  onReject,
  onApplyAll,
  isPersisting,
}: {
  reformulations: Reformulation[];
  decisions: Record<number, ReformulationDecision>;
  onAccept: (index: number, reformulation: Reformulation) => void;
  onReject: (index: number) => void;
  onApplyAll: () => void;
  isPersisting: boolean;
}) {
  const pendingCount = reformulations.filter((_, idx) => !decisions[idx]).length;
  return (
    <div
      className="rounded-lg p-4"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="section-label mb-1">{m.keyword_optimizer_reformulation_title()}</p>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            {m.keyword_optimizer_reformulation_hint()}
          </p>
          {/* AI transparency badge (EU AI Act Article 50) */}
          <div className="mt-1">
            <AiGeneratedBadge />
          </div>
        </div>
        {pendingCount > 0 && (
          <button
            onClick={onApplyAll}
            disabled={isPersisting}
            className="btn btn-primary shrink-0"
            data-testid="apply-all-reformulations"
          >
            {isPersisting
              ? m.keyword_optimizer_button_applying()
              : m.keyword_optimizer_button_apply_all({ count: String(pendingCount) })}
          </button>
        )}
      </div>
      <div className="space-y-4">
        {reformulations.map((r, idx) => {
          const decision = decisions[idx];
          return (
            <div
              key={idx}
              className="rounded-md p-3 transition-opacity duration-200"
              style={{
                background: "var(--surface-raised)",
                border: `1px solid ${decision === "accepted" ? "var(--success)" : decision === "rejected" ? "var(--border-subtle)" : "var(--border-subtle)"}`,
                opacity: decision === "rejected" ? 0.5 : 1,
              }}
            >
              {/* Original */}
              <div className="flex items-start gap-2">
                <span
                  className="mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                  style={{ border: "1px solid var(--border-subtle)", color: "var(--text-muted)" }}
                >
                  {m.keyword_optimizer_reformulation_before()}
                </span>
                <p className="text-xs leading-relaxed" style={{
                  color: "var(--text-muted)",
                  textDecoration: decision === "accepted" ? "line-through" : undefined,
                }}>
                  {r.original}
                </p>
              </div>

              {/* Reformulated */}
              <div className="flex items-start gap-2 mt-2.5">
                <span
                  className="mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                  style={{ border: "1px solid var(--border-subtle)", color: "var(--success)" }}
                >
                  {m.keyword_optimizer_reformulation_after()}
                </span>
                <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                  <HighlightedText text={r.reformulated} highlights={r.injectedKeywords} />
                </p>
              </div>

              {/* Injected keyword chips */}
              {r.injectedKeywords.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1 pl-[52px]">
                  {r.injectedKeywords.map((kw) => (
                    <span
                      key={kw}
                      className="inline-flex items-center gap-0.5 rounded-md px-2 py-0.5 text-[10px] font-medium"
                      style={{
                        border: "1px solid var(--border-subtle)",
                        color: "var(--accent)",
                        background: "var(--surface)",
                      }}
                    >
                      +{kw}
                    </span>
                  ))}
                </div>
              )}

              {/* Accept / Reject buttons */}
              <div className="mt-3 flex items-center gap-2 pl-[52px]">
                {decision === "accepted" ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "var(--success)" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {m.keyword_optimizer_reformulation_accepted()}
                  </span>
                ) : decision === "rejected" ? (
                  <span className="text-[11px] font-medium" style={{ color: "var(--text-muted)" }}>
                    {m.keyword_optimizer_reformulation_rejected()}
                  </span>
                ) : (
                  <>
                    <button
                      onClick={() => onAccept(idx, r)}
                      className="rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
                      style={{
                        background: "var(--success-subtle, rgba(34,197,94,0.1))",
                        color: "var(--success)",
                        border: "1px solid transparent",
                      }}
                    >
                      {m.keyword_optimizer_reformulation_accept()}
                    </button>
                    <button
                      onClick={() => onReject(idx)}
                      className="rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
                      style={{
                        background: "transparent",
                        color: "var(--text-muted)",
                        border: "1px solid var(--border-subtle)",
                      }}
                    >
                      {m.keyword_optimizer_reformulation_reject()}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HighlightedText({ text, highlights }: { text: string; highlights: string[] }) {
  if (highlights.length === 0) return <>{text}</>;

  // Build a regex matching any of the highlight keywords (case-insensitive)
  const escaped = highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        const key = `${part}-${i}`;
        const isHighlight = highlights.some((h) => h.toLowerCase() === part.toLowerCase());
        if (isHighlight) {
          return (
            <mark
              key={key}
              className="rounded px-0.5"
              style={{
                background: "var(--accent-subtle, rgba(38,109,240,0.12))",
                color: "var(--accent)",
              }}
            >
              {part}
            </mark>
          );
        }
        return <React.Fragment key={key}>{part}</React.Fragment>;
      })}
    </>
  );
}
